'use client'

import { IBaseUser } from '@/api/interfaces/IBaseUser'
import { IImage } from '@/api/interfaces/IImage'
import { UserType } from '@/api/types/user'
import { useMutation } from '@/hooks/useMutation'
import useSingleImageViewer from '@/hooks/useSingleImageViewer'
import { WithT } from '@/locales/types'
import { useAuthStore } from '@/store/auth'
import { fromNow } from '@/util/datetime'
import { formatNumber } from '@/util/number'
import classNames from 'classnames'
import { useState, useMemo } from 'react'
import { Avatar } from '../Avatar'
import { Icon } from '../Icon'
import { IncrementalButton } from '../buttons/IncrementalButton'
import { MoreButton } from '../buttons/MoreButton'
import { useDialogStore } from '../dialog/FullScreenBackDrop'
import { CommentEditDialog } from './CommentEditDialog'
import { CommentInput } from './CommentInput'
import Image from 'next/image'
import { createT } from '@/locales/index'
import { Paginated } from '@/api'
import { IBasePagination } from '@/api/interfaces/IBasePagination'
import { CommentStatus } from '@/api/types/comment'
import { GenericLink } from '../GenericLink'
import { useRouter } from 'next/navigation'
import { CommentApis, IBaseCommentRO } from '@/api/interfaces/IComment'
import { ImageWithFallback } from '../ImageWithFallback'
import DefaultImage from '@/public/images/v2/default/basic.png'

interface CommentRowProps<T extends IBaseCommentRO = IBaseCommentRO> {
  index: number
  domainId: number
  parentId?: number
  item: T
  parentNickname?: string
  onUpdate?: (comment: T, index: number, childIndex?: number) => void
  onDelete?: (index: number, childIndex?: number) => void
  onCreateChild?: (comment: T, index: number) => void
  onLoadMore?: (index: number, children: T[]) => void
  functions: Omit<CommentApis<T>, 'paginate'>
  disable?: boolean
  // showCreator?: boolean
  renderNicknameDecoration?: () => React.ReactNode
}

export const CommentRow = <T extends IBaseCommentRO = IBaseCommentRO>({
  index,
  domainId,
  parentId,
  item,
  translations,
  onUpdate,
  onDelete,
  onCreateChild,
  onLoadMore,
  functions,
  disable,
  // showCreator,
  renderNicknameDecoration,
}: WithT<CommentRowProps<T>>) => {
  const t = createT(translations)
  const router = useRouter()
  const mutate = useMutation()
  const dialog = useDialogStore()
  const { user } = useAuthStore()
  const [subEditForm, toggleSubEditForm] = useState(false)

  const [childredLoaded, setChildrenLoaded] = useState(false)

  const menu = useMemo(() => {
    if (item.user.username === user?.username) {
      return [
        {
          id: 'EDIT',
          text: t('edit'),
        },
        {
          id: 'DELETE',
          text: t('delete'),
        },
      ]
    }

    return [
      {
        id: 'REPORT',
        text: t('report'),
      },
    ]
  }, [item, user, t])

  const onLike = async () => {
    if (disable) {
      return
    }
    return await mutate(functions.like(item.id))
  }

  const onMenuClick = async (id: string) => {
    if (id === 'DELETE') {
      dialog.alert({
        title: t('dialog-delete-confirm-title'),
        cancelable: true,
        onConfirm: async () => {
          await mutate(functions.delete(item.id, domainId))
          dialog.pop()
          _onDelete(index)
        },
      })
    }

    if (id === 'EDIT') {
      dialog.push(`comment-edit-${domainId}-${item.id}`)
    }

    if (id === 'REPORT') {
      dialog.push('report')
    }
  }

  const onClickLoadMore = async () => {
    const items = await functions.loadMore(item.id, domainId)
    setChildrenLoaded(true)
    onLoadMore?.(index, items)
  }

  const _onUpdate = (comment: T) => {
    dialog.pop()
    onUpdate?.(comment, index)
  }

  const _onCreateChild = (comment: T, refIndex?: number) => {
    onCreateChild?.(comment, index)
    toggleSubEditForm(false)
  }

  const _onUpdateChild = (comment: T, childIndex: number) => {
    dialog.pop()
    onUpdate?.(comment, index, childIndex)
  }

  const _onDelete = (index: number) => {
    onDelete?.(index)
  }

  const _onDeleteChild = (childIndex: number) => {
    onDelete?.(index, childIndex)
  }

  useSingleImageViewer({ containerId: `comment-row-${item.id}` })

  const isDeleted = item.commentStatus === CommentStatus.DELETED

  const childrenRow = !!item.children?.length && (
    <div className="pl-4 flex flex-col">
      <ul>
        {item.children?.map((child, i) => (
          <CommentRow
            index={i}
            key={child.id}
            item={child as T}
            translations={translations}
            domainId={domainId}
            parentId={item.id}
            functions={functions}
            onUpdate={_onUpdateChild}
            onDelete={_onDeleteChild}
            onCreateChild={(c) => _onCreateChild(c, i)}
          />
        ))}
      </ul>
      <div>
        {!childredLoaded &&
          (item.childCount || 0) > 2 &&
          (item.childCount || 0) - item.children.length > 0 && (
            <button
              className="flex items-center gap-1"
              onClick={onClickLoadMore}
            >
              <span className="text-body2 font-bold text-key">
                {t('reply-count', {
                  count: (item.childCount || 0) - item.children.length,
                })}
              </span>
              <Icon size={14} name="caret-down" className="!text-key" />
            </button>
          )}
      </div>
    </div>
  )

  const buttonRow = (
    <div className="flex items-center gap-4">
      <IncrementalButton
        initialActive={item.isLike}
        initialCount={item.likeCount}
        onClick={onLike}
        className="flex items-center gap-1"
      >
        {(v, active) => (
          <>
            <Icon
              name={active ? 'heart-fill' : 'heart-line'}
              size={20}
              className={classNames({
                '!text-gray-600': !active,
                '!text-key': active,
              })}
            />
            <span className="text-body1 text-gray-500">{formatNumber(v)}</span>
          </>
        )}
      </IncrementalButton>
      <button
        className="flex items-center gap-1"
        onClick={() => {
          return disable ? null : toggleSubEditForm((prev) => !prev)
        }}
      >
        <Icon name="message-line" size={20} className="!text-gray-500" />
        <span className="text-body1 text-gray-500">{item.childCount || 0}</span>
      </button>
    </div>
  )

  const body = (
    <li
      className={classNames('py-5 flex flex-col gap-2', {
        // 'cursor-pointer': item.targetLink,
      })}
      // onClick={() => {
      //   console.log(item.targetLink)
      //   if (item.targetLink) {
      //     router.push(item.targetLink)
      //   }
      // }}
    >
      <div className="flex gap-2">
        <GenericLink
          href={`/@${item.user.username}`}
          className="flex items-center gap-2 shrink-0"
        >
          <Avatar size={28} image={item.user} />
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <p className="font-medium text-body2 text-gray-900">
                {item.user.nickname}
              </p>
            </div>
            <div
              className="text-caption1 text-gray-600"
              suppressHydrationWarning
            >
              {fromNow(item.createDate, translations)}
            </div>
          </div>
          {item.user.userType === UserType.CREATOR && (
            <Icon name="creator-badge" size={14} className="!text-key" />
          )}
        </GenericLink>
        {renderNicknameDecoration?.()}
        <div className="ml-auto shrink-0">
          <MoreButton menu={menu} onClick={onMenuClick} />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {item.image && (
          <a
            id={`comment-row-${item.id}`}
            data-src={item.image.url}
            data-width={item.image.width}
            data-height={item.image.height}
            className="cursor-pointer flex self-start"
          >
            <ImageWithFallback
              src={item.image.thumbnail || DefaultImage}
              width={300}
              height={300}
              alt={item.image.id.toString()}
              className="w-32"
              fallback={DefaultImage.src}
            />
          </a>
        )}
        <p className="text-body1 text-gray-900 whitespace-pre-line">
          {/* {parentNickname ? (
            <span className="mr-2 font-bold">@{parentNickname}</span>
          ) : (
            ''
          )} */}
          {item.body}
        </p>
      </div>

      {/* {showCreator && !!item.creator && (
        <div className="mt-2 flex items-center gap-2">
          <Avatar size={14} image={item.creator.profileImage} />
          <div className="flex-1 flex flex-col">
            <p className="text-caption2 font-medium text-gray-500">
              {item.creator.nickname}
            </p>
          </div>
        </div>
      )} */}
      {buttonRow}

      {subEditForm && (
        <CommentInput
          parentId={parentId || item.id}
          referenceId={parentId ? item.id : undefined}
          parentCommentNickname={item.user.nickname}
          domainId={domainId}
          translations={translations}
          createFn={functions.create}
          onCreateOrUpdate={_onCreateChild}
        />
      )}
      {childrenRow}
    </li>
  )

  const deleteBody = (
    <li className="py-5 flex flex-col gap-2 text-body1 text-start text-gray-500">
      {t('removed-comment')}
      {childrenRow}
    </li>
  )

  const wrapped = () => {
    if (isDeleted) {
      return deleteBody
    }

    return body
  }

  return (
    <>
      {wrapped()}
      <CommentEditDialog
        domainId={domainId}
        commendId={item.id}
        translations={translations}
        body={item.body}
        image={item.image}
        updateFn={functions.update}
        onUpdate={_onUpdate}
      />
    </>
  )
}
